import React from 'react';
import PropTypes from 'prop-types';
import { humanize, titleize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import { Button } from '@cargomatic/ui';
import { sluggify } from '../../../utils/localization/utils';

const canComplete = (order, shipments = []) => {
  if (!order) {
    return false;
  }

  if (shipments.length === 0 || order.equipments.length === 0) {
    return false;
  }

  const shipmentsClosed = shipments.every((s) => !s.current || s.canceled);
  const arBalanced = order.outstanding_ar === 0;
  const apBalanced = order.outstanding_ap === 0;

  const equipmentsMap = (order.equipments || []).reduce((map, eq) => {
    map[eq._id] = false;
    return map;
  }, {});

  shipments.forEach((s) => {
    const equipments = s.stops.reduce((runningEquipmentList, stop) => {
      const stopEquipments = stop.equipments || [];
      return runningEquipmentList.concat(...stopEquipments.map((eq) => eq._id));
    }, []);
    equipments.forEach((id) => {
      equipmentsMap[id] = true;
    });
  });

  const equipmentsAssociated = Object.values(equipmentsMap).every((eq) => !!eq);
  const stagedShipments = order.shipments_staging || [];
  const hasStagedShipments = stagedShipments.length > 0;
  return (
    shipmentsClosed &&
    arBalanced &&
    apBalanced &&
    equipmentsAssociated &&
    !hasStagedShipments
  );
};

const canCreated = () => true;

const validateWorkflowAction = (order, shipments) => (step) => {
  const workflows = {
    booking: () => canComplete(order, shipments),
    booked: () => canComplete(order, shipments),
    complete: () => canComplete(order, shipments),
    created: () => canCreated(),
  };

  return !workflows[step]();
};

const importWorkflows = {
  created: 'booking',
  en_route: 'booking',
  arrived: 'booking',
  booking: 'complete',
  booked: 'complete',
};

const exportWorkflows = {
  created: 'booking',
  booking: 'complete',
  booked: 'complete',
};

const getWorkflowStep = (order) => {
  let workflows;

  if (order.order_type === 'import') {
    workflows = { ...importWorkflows };
  } else {
    workflows = { ...exportWorkflows };
  }

  return workflows[order.status];
};

const Workflow = ({ order, onTransition }) => {
  const { t } = useTranslation();
  const workflow = getWorkflowStep(order);
  const disabled = workflow ? validateWorkflowAction(order)(workflow) : true;

  return (
    <Button
      variant="default"
      size="sm"
      disabled={disabled}
      onClick={() => onTransition(workflow)}
    >
      {titleize(humanize(t(sluggify(workflow))))}
    </Button>
  );
};

Workflow.propTypes = {
  order: PropTypes.object.isRequired,
  onTransition: PropTypes.func.isRequired,
};
export default Workflow;
